import React from 'react';
import { AlertTriangle, XCircle, ShieldOff } from 'lucide-react';

/**
 * RiskViolationPopup
 *
 * Full-screen modal shown when a risk rule is violated.
 * Not dismissible by clicking outside — user must click a button.
 *
 * Props:
 *   violations  — array of violation objects from checkRiskViolations()
 *   onStop      — callback when user clicks "I Understand — Stop Trading"
 *   onOverride  — callback when user clicks "Override — Continue Anyway"
 *                 (pass null to hide the Override button, e.g. for dashboard alerts)
 *   isPreSave   — true when the popup is shown before a trade is saved
 */
const RiskViolationPopup = ({ violations, onStop, onOverride, isPreSave = false }) => {
  if (!violations || violations.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-fade-in" style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(8px)' }}>
      {/* Card */}
      <div className="max-w-md w-full overflow-hidden animate-fade-in-up" style={{ background: '#0D1421', border: '1px solid rgba(239,68,68,0.4)', borderRadius: '20px', boxShadow: '0 32px 80px rgba(239,68,68,0.2), 0 0 0 1px rgba(239,68,68,0.1)' }}>

        {/* Red gradient header */}
        <div className="px-6 py-5 flex items-center space-x-3" style={{ background: 'linear-gradient(135deg, rgba(239,68,68,0.4), rgba(220,38,38,0.3))', borderBottom: '1px solid rgba(239,68,68,0.3)' }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #EF4444, #DC2626)', boxShadow: '0 4px 16px rgba(239,68,68,0.4)' }}>
            <AlertTriangle size={24} className="text-white" />
          </div>
          <div>
            <p className="font-bold text-white text-lg leading-tight" style={{ letterSpacing: '-0.02em' }}>Risk Rule Violation</p>
            <p className="text-xs" style={{ color: '#FCA5A5' }}>
              {isPreSave
                ? 'Adding this trade would break your risk rules'
                : 'Your trades have broken your risk rules'}
            </p>
          </div>
        </div>

        <div className="p-5">
          {/* Violations list */}
          <div className="space-y-3 mb-5 max-h-72 overflow-y-auto">
            {violations.map((v, i) => (
              <div key={i} className="p-4 rounded-xl" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
                <div className="flex items-start space-x-2 mb-1.5">
                  <XCircle size={14} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="font-bold text-red-300 text-sm">{v.title}</p>
                </div>
                <p className="text-sm mb-3 leading-relaxed" style={{ color: '#9CA3AF' }}>{v.message}</p>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs px-2.5 py-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: '#9CA3AF' }}>
                    {v.limitLabel}:{' '}
                    <span className="font-semibold text-gray-200">
                      {v.unit ? `${v.limit} ${v.unit}` : `₹${Number(v.limit).toLocaleString('en-IN')}`}
                    </span>
                  </span>
                  <span className="text-xs px-2.5 py-1 rounded-lg" style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#FCA5A5' }}>
                    Actual:{' '}
                    <span className="font-semibold">
                      {v.unit ? `${v.actual} ${v.unit}` : `₹${Number(v.actual).toFixed(0)}`}
                    </span>
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Action buttons */}
          <div className="space-y-2.5">
            <button
              onClick={onStop}
              className="w-full text-white py-3 rounded-xl font-bold text-sm transition-all flex items-center justify-center space-x-2"
              style={{ background: 'linear-gradient(135deg, #EF4444, #DC2626)', boxShadow: '0 4px 16px rgba(239,68,68,0.3)' }}
            >
              <XCircle size={16} />
              <span>I Understand — Stop Trading</span>
            </button>

            {onOverride && (
              <button
                onClick={onOverride}
                className="w-full py-3 rounded-xl font-medium text-sm transition-all flex items-center justify-center space-x-2"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#9CA3AF' }}
              >
                <ShieldOff size={15} />
                <span>Override — Continue Anyway</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskViolationPopup;
